<template>
  <div v-if="!collapsed" class="w-full">
    <div class="w-full rounded-2xl">
      <Disclosure v-slot="{ open }" :default-open="isActive">
        <DisclosureButton
          :class="[
            'flex w-full testing items-center gap-3 cursor-pointer mb-1 hover:bg-neutral-100 fill text-xsm py-2 relative overflow-hidden text-typography-disabled px-4',
          ]"
        >
          <component :is="!open ? Icons.LineFolder : Icons.LineFolderOpen" class="h-5 w-5 fill-fosho shrink-0" />
          <span class="whitespace-nowrap">{{ label }}</span>
          <PulseBadge v-if="!open && totalCount > 0" variant="primary">{{ totalCount }}</PulseBadge>
          <component :is="!open ? Icons.LineAngleRight : Icons.LineAngleUp" class="h-5 w-5 fill-fosho shrink-0" />
        </DisclosureButton>
        <DisclosurePanel class="pt-1 text-sm text-gray-500 ml-2">
          <template v-for="option in items" :key="(option as any).label">
            <template v-if="(option as any).items?.length">
              <Disclosure v-slot="{ open: subOpen }" :default-open="isSubActive(option as any)">
                <DisclosureButton
                  class="flex w-full items-center gap-3 cursor-pointer mb-1 hover:bg-neutral-100 text-xsm py-2 relative overflow-hidden text-typography-disabled px-4 ml-2"
                >
                  <component :is="(option as any).icon" class="h-5 w-5 fill-fosho shrink-0" />
                  <span v-if="!collapsed" class="whitespace-nowrap">{{ (option as any).label }}</span>
                  <PulseBadge v-if="!subOpen && sumCounts((option as any).items) > 0" variant="primary">
                    {{ sumCounts((option as any).items) }}
                  </PulseBadge>
                  <component
                    :is="!subOpen ? Icons.LineAngleRight : Icons.LineAngleUp"
                    class="h-5 w-5 fill-fosho shrink-0"
                  />
                </DisclosureButton>
                <DisclosurePanel class="ml-4">
                  <div v-for="subOption in (option as any).items" :key="subOption.label" class="ml-2">
                    <NavItem
                      :to="subOption.to"
                      :count="subOption.count || 0"
                      :active="matchesActivePaths(subOption)"
                      @click="$emit('menu:click')"
                    >
                      <component :is="subOption.icon" class="h-5 w-5" />
                      <span v-if="!collapsed">{{ subOption.label }}</span>
                    </NavItem>
                  </div>
                </DisclosurePanel>
              </Disclosure>
            </template>
            <template v-else>
              <div class="ml-2">
                <NavItem
                  :to="(option as Navigatable).to"
                  :count="(option as Navigatable).count || 0"
                  @click="$emit('menu:click')"
                >
                  <component :is="(option as Navigatable).icon" class="h-5 w-5" />
                  <span v-if="!collapsed">{{ (option as Navigatable).label }}</span>
                </NavItem>
              </div>
            </template>
          </template>
        </DisclosurePanel>
      </Disclosure>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
import * as Icons from '@pulse/components/icons';
import { useRoute } from 'vue-router';
import { computed } from 'vue';

type Navigatable = {
  icon: unknown;
  label: string;
  to: string;
  count?: number;
  /** Other paths this item stands for, e.g. the pages behind its tabs; it is active on any of them too. */
  activePaths?: string[];
};

defineEmits(['menu:click']);

const props = defineProps({
  collapsed: {
    required: true,
    type: Boolean,
  },
  icon: {
    default: 'Icons.LineFolder',
    required: false,
    type: String,
  },
  items: {
    required: true,
    type: Array,
  },
  label: {
    required: true,
    type: String,
  },
});

// Sum the pending-count badges of every leaf under a group so a collapsed
// group surfaces the total of what's hidden inside it.
function sumCounts(items: any[]): number {
  return (items ?? []).reduce((total, item) => {
    if (item?.items?.length) return total + sumCounts(item.items);

    return total + (item?.count || 0);
  }, 0);
}

const totalCount = computed(() => sumCounts(props.items as any[]));

const route = useRoute();
const isActive = computed(() =>
  props.items.some((item: any) => {
    if (item.items?.length) return item.items.some((sub: Navigatable) => isPathActive(sub));

    return route.path && route.path.startsWith(item.to);
  }),
);

function isSubActive(group: { items: Navigatable[] }) {
  return group.items.some(sub => isPathActive(sub));
}

function matchesActivePaths(item: Navigatable): boolean {
  return Boolean(route.path && item.activePaths?.some(path => route.path.startsWith(path)));
}

function isPathActive(item: Navigatable): boolean {
  return Boolean(route.path && route.path.startsWith(item.to)) || matchesActivePaths(item);
}
</script>
